
const transformVector3 = (m, v) => [
    m[0] * v[0] + m[4] * v[1] + m[8] * v[2] + m[12],
    m[1] * v[0] + m[5] * v[1] + m[9] * v[2] + m[13],
    m[2] * v[0] + m[6] * v[1] + m[10] * v[2] + m[14]
];
// eslint-disable-next-line import/first
import { default as MacroReader } from "./MacroReader.js";

export class EmaLoader {
    /** @type {object} */
    _renderer;

    _allocator;

    /** @type {number[]} */
    variantNodeIDs = [];

    constructor(renderer, allocator) {
        this._renderer = renderer;
        this._allocator = allocator;
    }

    /**
     * Loads an .EMA model file.
     * @param {string} emaURL           The URL of the model file.
     */
    async loadEma(emaURL) {
        function getFloatVector3(vector) {
            return [Math.fround(vector.getX()), Math.fround(vector.getY()), Math.fround(vector.getZ())];
        }

        function bgra2rgba(data) {
            for (var i = 0; i < data.length; i += 4)
                [data[i + 0], data[i + 2]] = [data[i + 2], data[i + 0]];
            return data;
        };

        const textureIds = [];
        function loadEmaTextures(emaTextures) {
            const textures = [];
            for (let j = 0; j < emaTextures.size(); j++) {
                const texture = emaTextures.get(j);

                const dataVector = texture.getDataVector();

                const width = texture.getWidth();
                const height = texture.getHeight();
                const textureData = new Uint8ClampedArray(width * height * 4);

                var dvCounter = 0;
                for (var i = 0; i < textureData.length && dvCounter < dataVector.size(); i++) {
                    if ((i + 1) % 4 == 0) {
                        textureData[i] = 255;//add transparency value to rgb data
                    }
                    else {
                        textureData[i] = dataVector.get(dvCounter++);
                    }

                }

                textures.push(new ImageData(bgra2rgba(textureData), width, height));

                textureIds.push(texture.getName());
            }

            const textureMap = textures.map(texture => {
                const id = allocator.getTextureIndex();
                renderer.storeTexture(id, texture);
                return id;
            });

            return textureMap;
        }

        function getTextureIdFromName(textureName) {
            return textureIds.indexOf(textureName);
        }

        function intToRGBAColor(numColor) {
            const rgba = new ArrayBuffer(4);
            const view = new DataView(rgba);
            view.setUint32(0, numColor, true); // byteOffset = 0; litteEndian = true
            const arr = Array.from(new Uint8Array(rgba))
            arr[3] = 255 - arr[3];
            return arr.map(i => i / 255);
        }

        const mapColorId = new Map();
        function getMaterialIdFromColor(color) {
            if (mapColorId.has(color)) {
                return mapColorId.get(color);
            }
            else {
                const rgba = intToRGBAColor(color);
                const newMaterialIndex = allocator.getMaterialIndex();
                renderer.setMaterial(newMaterialIndex, { diffuse: rgba });
                mapColorId.set(color, newMaterialIndex);
                return newMaterialIndex;
            }
        }

        async function createNodeFromMesh(mesh, funcLayer, funcColor, textureMap) {
            const vertices = mesh.getVertices();
            const normals = mesh.getNormals();

            var triData = [];
            var texTriData = [];
            var lineData = [];

            var lastMat = null;
            var currentPart = null;
            const pushTriangleData = (vertex, normal, materialIndex) => {
                if (materialIndex != lastMat || currentPart == null) {
                    if (lastMat != null)
                        triData.push(currentPart);
                    currentPart = { material: materialIndex, vertexData: [] };
                }

                currentPart.vertexData.push(...getFloatVector3(vertex), ...getFloatVector3(normal));
                lastMat = materialIndex;
            }

            var textureID;
            if (mesh.getTextureName())
                textureID = getTextureIdFromName(mesh.getTextureName());
            const hasTexture = textureID != -1;

            const faces = mesh.getFaces();
            for (let k = 0; k < faces.size(); k++) {
                // Skip top coboid face with, is added later with a texture
                if (hasTexture && k == 4)
                    continue;

                const face = faces.get(k);
                const vertexIndices = face.getFaceIndices();
                const normalIndices = face.getNormalIndices();

                const meshColor = mesh.getDefaultColor();
                const faceColor = face.getColor();
                const color = macroReader.ColorCalculator.getFaceColor(funcLayer, funcColor, meshColor, faceColor);
                const colorID = getMaterialIdFromColor(color);

                for (let vi = 0; vi < vertexIndices.size(); vi++)
                    pushTriangleData(vertices.get(vertexIndices.get(vi)), normals.get(normalIndices.get(vi)), colorID);

                const edges = face.getEdgeIndices();
                var ld = [];
                for (let l = 0; l < edges.size(); l++) {
                    const edgeIndices = edges.get(l);

                    for (var ei = 0; ei < edgeIndices.size(); ei++)
                        ld.push(...getFloatVector3(vertices.get(edgeIndices.get(ei))));

                    if (l != edges.size() - 1)
                        ld.push(Infinity, Infinity, Infinity);

                    lineData.push({ vertexData: ld });
                }
            }

            if (hasTexture) {
                const matrix = mesh.getTextureTransformation().getMatrix();

                const normal = [0., 0., 1.];
                const colorID = getMaterialIdFromColor(0x01ffffff);//opaque white

                //vertex: [posX,posY,posZ, normal, uv]
                const vertices = [
                    [...transformVector3(matrix, [0, 0, 0]), ...normal, 0, 0],
                    [...transformVector3(matrix, [1, 0, 0]), ...normal, 1, 0],
                    [...transformVector3(matrix, [0, 1, 0]), ...normal, 0, 1],
                    [...transformVector3(matrix, [1, 1, 0]), ...normal, 1, 1]
                ];

                const indices = [0, 1, 2, 1, 3, 2];

                var vd = [];
                for (var vi = 0; vi < indices.length; vi++)
                    vd.push(vertices[indices[vi]]);
                texTriData.push({ material: colorID, texture: textureMap[textureID], vertexData: vd });
            }

            triData.push(currentPart);

            var meshNode = renderer.createGraphicalNode();
            meshNode.name = mesh.getID();
            meshNode.geometryId = allocator.getGeometryIndex();
            var geometryObj = {
                triangle: triData,
                texturedTriangle: texTriData,
                line: lineData
            };

            storePromises.push(renderer.storeGeometry(meshNode.geometryId, geometryObj));

            return meshNode;
        }

        var storePromises = [];

        const renderer = this._renderer;
        const allocator = this._allocator;

        const macroReader = await MacroReader();
        const emaParser = new macroReader.MacroParser();

        var root = renderer.createInternalNode();
        root.name = `Macro: ${emaURL}`;
        root.matrix = [
            1, 0, 0, 0,
            0, 0, -1, 0,
            0, 1, 0, 0,
            0, 0, 0, 1
        ];

        await fetch(emaURL)
            .then(response => response.arrayBuffer())
            .then(async arrayBuffer => {
                emaParser.parseMacroFromString(arrayBuffer);

                const errMsg = emaParser.getErrorMessage();
                if (errMsg != "")
                    console.error("ErrorMessage: ", errMsg);

                const variants = emaParser.getVariants();
                for (let i = 0; i < variants.size(); i++) {
                    const variant = variants.get(i);

                    var variantNode = renderer.createInternalNode();
                    variantNode.name = variant.getID().constructor.name + variant.getFuncType().constructor.name;

                    var textureMap = loadEmaTextures(variant.getTextures());

                    const functions = variant.getFunctions();
                    for (let j = 0; j < functions.size(); j++) {
                        const func = functions.get(j);
                        const meshID = func.getMeshID();

                        if (meshID) {
                            const mesh = variant.getMesh(meshID);

                            const meshNode = await createNodeFromMesh(mesh, func.getLayer(), func.getColor(), textureMap);
                            const emaMatrix = variant.getTransformationMatrixFromFunction(func.getID());
                            meshNode.matrix = emaMatrix.getMatrix();
                            variantNode.addChild(meshNode);
                        }
                    }

                    root.addChild(variantNode);
                    this.variantNodeIDs.push(variantNode.id);
                }
            });

        emaParser.delete();

        renderer.sceneRootNode.addChild(root);

        await Promise.all(storePromises);

        return root;
    }
}
